/**
 * Join links in Add machine (ADR 0090 decision 2): pressing the download button mints a join
 * ticket for this person, copies its link to the clipboard and lets the download go on. The agent
 * on the new computer reads the link once at first run and joins by itself; nothing to type and
 * no second browser page. Lands dark: on only with `?joinTicket=1` (remembered for the tab) until
 * the e2e test passes.
 *
 * Pure model and the source seam (live API or demo data); the hook is useJoinTickets.ts and the
 * view JoinLinkPanel.tsx.
 */
import { CLOUD_MACHINE_NAME_RE, JOIN_PAGE, JOIN_TICKET_TTL_MS, cloudInitUserData, cloudRepoFolder } from '@orgus/protocol';
import type { JoinTicket, JoinTicketCreated } from '@orgus/protocol';
import { formatClock, formatElapsed, formatInt, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { cancelJoinTicket, listJoinTickets, mintJoinTicket } from '../pairing/api.ts';

/** The query flag that turns join links on, and where the tab remembers it. */
export const JOIN_TICKET_FLAG = 'joinTicket';
const REMEMBERED = 'orgus.features.joinTickets';

interface FlagStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

function tabStore(): FlagStore | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Join links are on: `?joinTicket=1` in this address, or earlier in this tab. `?joinTicket=0`
 * turns them off again. Storage that throws (a private window, blocked site data) only loses the
 * memory, never the flag in the address.
 */
export function joinTicketsOn(search: string = typeof window === 'undefined' ? '' : window.location.search, store: FlagStore | null = tabStore()): boolean {
  const v = new URLSearchParams(search).get(JOIN_TICKET_FLAG);
  try {
    if (v === '1') store?.setItem(REMEMBERED, '1');
    else if (v === '0') store?.removeItem(REMEMBERED);
  } catch {
    // Storage refused: the address still decides.
  }
  if (v === '1') return true;
  if (v === '0') return false;
  try {
    return store?.getItem(REMEMBERED) === '1';
  } catch {
    return false;
  }
}

const TTL_MINUTES = Math.round(JOIN_TICKET_TTL_MS / 60_000);

/** What the dialog says once the link is on the clipboard. */
export const JOIN_LINK_COPIED = `Link copied. Open the download on the new computer; it joins by itself within ${TTL_MINUTES} minutes.`;
/** The browser refused the clipboard: the person copies it. */
export const JOIN_LINK_NOT_COPIED = `The browser did not let Orgus copy the link. Copy it, then open the download on the new computer; it joins by itself within ${TTL_MINUTES} minutes.`;

/** The join link, from pressing download to the link on the clipboard (or not). */
export type JoinLinkState =
  | { kind: 'idle' }
  | { kind: 'making' }
  | { kind: 'copied'; link: JoinTicketCreated }
  | { kind: 'not-copied'; link: JoinTicketCreated }
  | { kind: 'error'; error: UiError };

/** Mint a ticket and copy its link. Never throws: a failed mint is an inline error, and the download goes on regardless. */
export async function makeJoinLink(deps: { mint(): Promise<JoinTicketCreated>; copy(text: string): Promise<boolean> }): Promise<JoinLinkState> {
  let link: JoinTicketCreated;
  try {
    link = await deps.mint();
  } catch (e) {
    return { kind: 'error', error: toUiError(e) };
  }
  let copied = false;
  try {
    copied = await deps.copy(link.url);
  } catch {
    copied = false;
  }
  return copied ? { kind: 'copied', link } : { kind: 'not-copied', link };
}

/** The tickets still open at `now` (the server lists open ones; time moves on in the browser), newest first. */
export function openTickets(list: readonly JoinTicket[], now: number): JoinTicket[] {
  return list.filter((t) => t.state === 'open' && !t.used && t.expiresAt > now).sort((a, b) => b.createdAt - a.createdAt);
}

/** "Expires in 12m". */
export function ticketExpiry(t: Pick<JoinTicket, 'expiresAt'>, now: number): string {
  return `Expires in ${formatElapsed(Math.max(0, t.expiresAt - now))}`;
}

/** "1 link open, expires in 12m"; "2 links open, the next expires in 3m"; null when none. */
export function ticketsSummary(list: readonly JoinTicket[], now: number): string | null {
  const open = openTickets(list, now);
  if (!open.length) return null;
  const soonest = Math.min(...open.map((t) => t.expiresAt));
  const left = formatElapsed(Math.max(0, soonest - now));
  return open.length === 1 ? `1 link open, expires in ${left}` : `${formatInt(open.length)} links open, the next expires in ${left}`;
}

/** The /join/<ticket> page's sentence: until when the link works, when the page knows. */
export function joinPageSentence(expiresAt: number | null): string {
  const lead = 'Open the download you just made on that computer.';
  return expiresAt ? `${lead} This link works until ${formatClock(expiresAt)}.` : `${lead} This link works for ${TTL_MINUTES} minutes after it was made.`;
}

/** Mint, list and cancel: the hub (or control plane), or demo data in mock mode. */
export interface JoinTicketSource {
  mint(): Promise<JoinTicketCreated>;
  list(signal?: AbortSignal): Promise<JoinTicket[]>;
  cancel(id: string): Promise<JoinTicket>;
}

export const liveJoinTickets: JoinTicketSource = {
  mint: () => mintJoinTicket(),
  list: (signal) => listJoinTickets(signal),
  cancel: (id) => cancelJoinTicket(id),
};

/** Demo data: tickets in memory, a link of this page's origin. */
export function demoJoinTickets(origin: string = typeof window === 'undefined' ? 'https://app.orgus.ai' : window.location.origin, now: () => number = Date.now): JoinTicketSource {
  let tickets: JoinTicket[] = [];
  let n = 0;
  return {
    async mint() {
      const at = now();
      n += 1;
      const id = `jt_demo${n}`;
      tickets = [{ id, state: 'open', createdAt: at, expiresAt: at + JOIN_TICKET_TTL_MS, used: false }, ...tickets];
      return { id, url: `${origin}${JOIN_PAGE}/demo${n}Xq7RkP2mVw9sLc4Hn8Tb`, createdAt: at, expiresAt: at + JOIN_TICKET_TTL_MS };
    },
    async list() {
      return openTickets(tickets, now());
    },
    async cancel(id) {
      const t = tickets.find((x) => x.id === id);
      tickets = tickets.filter((x) => x.id !== id);
      return { ...(t ?? { id, createdAt: now(), expiresAt: now(), used: false }), state: 'cancelled' };
    },
  };
}

// ---------------------------------------------------------------------------
// Cloud VM (ADR 0105 decision 1): a join ticket wrapped in cloud-init user data
// ---------------------------------------------------------------------------

/** The cloud-init document, from pressing "Make cloud-init" to the document on the clipboard (or not). */
export type CloudInitState =
  | { kind: 'idle' }
  | { kind: 'making' }
  | { kind: 'ready'; link: JoinTicketCreated; userData: string; copied: boolean }
  | { kind: 'error'; error: UiError };

/** What the panel says once the document is on the clipboard, and when the browser refused. */
export const CLOUD_INIT_COPIED = 'Cloud-init copied. Paste it as the user data of the new VM; the machine joins by itself after it boots.';
export const CLOUD_INIT_NOT_COPIED = 'The browser did not let Orgus copy it. Copy the document, then paste it as the user data of the new VM.';

/**
 * Mint a ticket and wrap it in the cloud-init document, then copy the document. Never throws: a
 * name the document cannot carry, or a failed mint, is an inline error and nothing is minted
 * for a bad name (checked first, so no ticket is wasted).
 */
export async function makeCloudInit(deps: { mint(): Promise<JoinTicketCreated>; copy(text: string): Promise<boolean>; name?: string; hubUrl?: string; repoUrl?: string }): Promise<CloudInitState> {
  const name = deps.name?.trim() || undefined;
  const repoUrl = deps.repoUrl?.trim() || undefined;
  if (repoUrl !== undefined) {
    try {
      cloudRepoFolder(repoUrl);
    } catch {
      return { kind: 'error', error: toUiError(new Error('Repository: a public https address such as https://github.com/org/repo, with no password in it.')) };
    }
  }
  if (name !== undefined && !CLOUD_MACHINE_NAME_RE.test(name)) {
    return { kind: 'error', error: toUiError(new Error('Machine name: letters, digits and dashes only, 63 at most, starting with a letter or digit.')) };
  }
  let link: JoinTicketCreated;
  try {
    link = await deps.mint();
  } catch (e) {
    return { kind: 'error', error: toUiError(e) };
  }
  let userData: string;
  try {
    userData = cloudInitUserData({ joinUrl: link.url, ...(name ? { name } : {}), ...(deps.hubUrl ? { hubUrl: deps.hubUrl } : {}), ...(repoUrl ? { repoUrl } : {}) });
  } catch (e) {
    return { kind: 'error', error: toUiError(e) };
  }
  let copied = false;
  try {
    copied = await deps.copy(userData);
  } catch {
    copied = false;
  }
  return { kind: 'ready', link, userData, copied };
}
